import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { Plus, Edit2, Trash2, Box, QrCode, Printer, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Containers = () => {
  const { getAuthHeaders, isManager } = useAuth();
  const navigate = useNavigate();
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedContainer, setSelectedContainer] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [formData, setFormData] = useState({ name: '', label: '', description: '', location: '' });

  const fetchContainers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/containers`, { headers: getAuthHeaders() });
      setContainers(response.data);
    } catch (error) {
      toast.error('Failed to fetch containers');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchContainers(); }, [fetchContainers]);

  const handleOpenDialog = (container = null) => {
    if (container) {
      setSelectedContainer(container);
      setFormData({
        name: container.name,
        label: container.label,
        description: container.description || '',
        location: container.location || ''
      });
    } else {
      setSelectedContainer(null);
      setFormData({ name: '', label: '', description: '', location: '' });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (selectedContainer) {
        await axios.put(`${API_URL}/api/containers/${selectedContainer.id}`, formData, { headers: getAuthHeaders() });
        toast.success('Container updated');
      } else {
        await axios.post(`${API_URL}/api/containers`, formData, { headers: getAuthHeaders() });
        toast.success('Container created');
      }
      setShowDialog(false);
      fetchContainers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save container');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/containers/${selectedContainer.id}`, { headers: getAuthHeaders() });
      toast.success('Container deleted');
      setShowDeleteDialog(false);
      fetchContainers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handlePrintQR = (container) => {
    const qrUrl = `${window.location.origin}/container/${container.id}`;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR - ${container.label}</title>
          <style>
            body { font-family: Arial, sans-serif; text-align: center; padding: 20px; }
            .qr-container { display: inline-block; padding: 20px; border: 2px solid #000; margin: 10px; }
            .label { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
            .name { font-size: 16px; color: #666; margin-bottom: 15px; }
            .qr-code img { width: 200px; height: 200px; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <div class="label">${container.label}</div>
            <div class="name">${container.name}</div>
            <div class="qr-code">
              <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrUrl)}" />
            </div>
            <div style="font-size: 12px; color: #888; margin-top: 10px;">Scan to see contents</div>
          </div>
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleBatchPrint = () => {
    const selected = containers.filter(c => selectedIds.has(c.id));
    if (selected.length === 0) return;
    
    const printWindow = window.open('', '_blank');
    const labels = selected.map(container => {
      const qrUrl = `${window.location.origin}/container/${container.id}`;
      return `
        <div class="qr-container">
          <div class="label">${container.label}</div>
          <div class="name">${container.name}</div>
          <div class="qr-code">
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrUrl)}" />
          </div>
          <div style="font-size: 10px; color: #888;">Scan for contents</div>
        </div>
      `;
    }).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Container Labels</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 10px; }
            .grid { display: flex; flex-wrap: wrap; gap: 10px; }
            .qr-container { display: inline-block; padding: 15px; border: 1px solid #000; text-align: center; width: 200px; page-break-inside: avoid; }
            .label { font-size: 18px; font-weight: bold; margin-bottom: 5px; }
            .name { font-size: 12px; color: #666; margin-bottom: 10px; }
            .qr-code img { width: 130px; height: 130px; }
            @media print { body { padding: 0; margin: 0; } }
          </style>
        </head>
        <body>
          <div class="grid">${labels}</div>
          <script>window.onload = function() { setTimeout(function() { window.print(); }, 500); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
    setSelectedIds(new Set());
  };

  const toggleSelect = (id) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id); else newSet.add(id);
    setSelectedIds(newSet);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === containers.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(containers.map(c => c.id)));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="containers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Prop Containers</h1>
          <p className="text-muted-foreground mt-1">Manage storage containers for props</p>
        </div>
        <div className="flex gap-2">
          {selectedIds.size > 0 && (
            <Button variant="outline" onClick={handleBatchPrint} data-testid="batch-print-containers">
              <Printer className="w-4 h-4 mr-2" />
              Print Labels ({selectedIds.size})
            </Button>
          )}
          {isManager() && (
            <Button data-testid="add-container-btn" onClick={() => handleOpenDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Container
            </Button>
          )}
        </div>
      </div>

      <Card className="border-border/50">
        <CardHeader className="flex flex-row items-center gap-2">
          <Box className="w-5 h-5 text-muted-foreground" />
          <CardTitle className="text-lg">All Containers ({containers.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={containers.length > 0 && selectedIds.size === containers.length}
                    onCheckedChange={toggleSelectAll}
                  />
                </TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-center">Props</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {containers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                    No containers yet. Add your first container to organize props.
                  </TableCell>
                </TableRow>
              ) : (
                containers.map((c) => (
                  <TableRow key={c.id} className="table-row-hover" data-testid={`container-row-${c.id}`}>
                    <TableCell>
                      <Checkbox checked={selectedIds.has(c.id)} onCheckedChange={() => toggleSelect(c.id)} />
                    </TableCell>
                    <TableCell>
                      <code className="text-sm bg-muted px-2 py-1 rounded font-mono font-bold">{c.label}</code>
                    </TableCell>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell>{c.location || '-'}</TableCell>
                    <TableCell className="text-muted-foreground max-w-[200px] truncate">{c.description || '-'}</TableCell>
                    <TableCell className="text-center">
                      <span className="inline-block bg-primary/10 text-primary px-2 py-1 rounded text-xs font-semibold">
                        {c.prop_count}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => navigate(`/container/${c.id}`)} title="View Contents" data-testid={`view-container-${c.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => { setSelectedContainer(c); setShowQRDialog(true); }} title="QR Code" data-testid={`qr-container-${c.id}`}>
                          <QrCode className="w-4 h-4" />
                        </Button>
                        {isManager() && (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(c)} title="Edit">
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => { setSelectedContainer(c); setShowDeleteDialog(true); }} className="text-destructive" title="Delete">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{selectedContainer ? 'Edit Container' : 'Add Container'}</DialogTitle>
            <DialogDescription>Create labeled containers to organize your props.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Label * (e.g., PROP-BIN-01)</Label>
              <Input
                data-testid="container-label-input"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value.toUpperCase() })}
                placeholder="PROP-BIN-01"
                className="font-mono"
              />
            </div>
            <div className="grid gap-2">
              <Label>Name *</Label>
              <Input
                data-testid="container-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Small Props Bin"
              />
            </div>
            <div className="grid gap-2">
              <Label>Location</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g., Prop Room Shelf 3"
              />
            </div>
            <div className="grid gap-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="What's typically stored in this container..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button data-testid="save-container-btn" onClick={handleSave} disabled={!formData.name || !formData.label}>
              {selectedContainer ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Container QR Code</DialogTitle>
            <DialogDescription>Scan to see what's inside this container</DialogDescription>
          </DialogHeader>
          {selectedContainer && (
            <div className="flex flex-col items-center py-6 space-y-4">
              <div className="bg-white p-4 rounded-xl">
                <QRCodeSVG value={`${window.location.origin}/container/${selectedContainer.id}`} size={200} level="H" />
              </div>
              <div className="text-center">
                <p className="font-bold text-xl">{selectedContainer.label}</p>
                <p className="text-sm text-muted-foreground">{selectedContainer.name}</p>
              </div>
              <Button onClick={() => handlePrintQR(selectedContainer)} className="w-full">
                <Printer className="w-4 h-4 mr-2" />
                Print Label
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Container</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedContainer?.name}"? Props inside cannot be moved automatically.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Containers;
